import React from "react";

import { reverse } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { isoWebPageURL } from "../../../models/nominals";
import { IUser } from "../../../models/user";
import { navigateToURL } from "../../../utils/urls";
import { useAppDispatch, useAppSelector } from "../../reducers";
import * as signals from "../../signals";
import { defaults } from "../defaults";
import { IReduxState } from "../reducers.interfaces";
import { completeOrderPayment } from "../thunks";
import { ICheckoutHTTPError } from "../types";

const handlePaymentFrameLoaded = (
    user: IUser | null,
    dispatch: ReturnType<typeof useAppDispatch>,
    checkoutState: IReduxState,
    onCheckoutError: (err: ICheckoutHTTPError) => void,
) => {
    dispatch(completeOrderPayment({ user }))
        .unwrap()
        .catch((err: ICheckoutHTTPError) => {
            // In case of an error, reload the page to display it.
            onCheckoutError(err);
            const window = getWindow();
            if (window) {
                window.scrollTo({
                    top: 0,
                });
            }
            // turning off page reload so we don't lose cc error info
            // window.location.reload();
            // TODO should this be conditional?
        });
};

const handlePaymentTimeout = (
    checkoutState: IReduxState,
    hasPaymentMethodError: boolean,
) => {
    // if payment is pending and no error messages shown
    if (
        checkoutState.data.payment_states &&
        checkoutState.data.payment_states.order_status === "Pending" &&
        !hasPaymentMethodError
    ) {
        console.log(
            "checkout is stuck in pending, redirecting to order-pending page",
        );
        navigateToURL(isoWebPageURL.wrap(reverse("checkout:order-pending")));
    }
};

type MessageContext = {
    user: IUser | null;
    checkoutState: IReduxState;
    onCheckoutError: (err: ICheckoutHTTPError) => void;
    hasPaymentMethodError: boolean;
};

export const PaymentChildFrameListener = (props: {
    onCheckoutError: (err: ICheckoutHTTPError) => void;
    hasPaymentMethodError: boolean;
}): React.ReactNode => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.common.user);
    const checkoutState = useAppSelector((state) => state.checkout || defaults);

    const prevPaymentDeclinedRef = React.useRef<boolean | null>(null);
    const messageContextRef = React.useRef<MessageContext>({
        user,
        checkoutState,
        onCheckoutError: props.onCheckoutError,
        hasPaymentMethodError: props.hasPaymentMethodError,
    });
    const dispatchRef = React.useRef(dispatch);

    React.useEffect(() => {
        messageContextRef.current = {
            user,
            checkoutState,
            onCheckoutError: props.onCheckoutError,
            hasPaymentMethodError: props.hasPaymentMethodError,
        };
    }, [
        user,
        checkoutState,
        props.onCheckoutError,
        props.hasPaymentMethodError,
    ]);

    React.useEffect(() => {
        dispatchRef.current = dispatch;
    }, [dispatch]);

    // Handle message events from payment iframe
    React.useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }

        const messageHandler = (event: MessageEvent) => {
            // Message is sent from a hidden iframe used to submit Cybersource data
            // See tsi-common/src/tsicommon/templates/oscar/checkout/payment-redirect.html
            const {
                user: latestUser,
                checkoutState: latestCheckoutState,
                onCheckoutError,
                hasPaymentMethodError,
            } = messageContextRef.current;
            const currentDispatch = dispatchRef.current;
            const messageType: string = event.data.type;
            switch (messageType) {
                case "TSI_CHECKOUT_PAYMENT_FRAME_LOADED":
                    handlePaymentFrameLoaded(
                        latestUser,
                        currentDispatch,
                        latestCheckoutState,
                        onCheckoutError,
                    );
                    break;
                case "TSI_CHECKOUT_PAYMENT_TIMEOUT":
                    handlePaymentTimeout(
                        latestCheckoutState,
                        hasPaymentMethodError,
                    );
                    break;
            }
        };

        window.addEventListener("message", messageHandler, false);

        return () => {
            window.removeEventListener("message", messageHandler);
        };
    }, []);

    // Handle payment decline detection
    React.useEffect(() => {
        const currDeclined =
            checkoutState.data.payment_states &&
            checkoutState.data.payment_states.order_status ===
                "Payment Declined";

        // Trigger onPaymentDecline, but not if we already did trigger it.
        if (currDeclined && !prevPaymentDeclinedRef.current) {
            signals.checkout.onPaymentDecline.emit();
        }

        prevPaymentDeclinedRef.current = currDeclined;
    }, [checkoutState.data.payment_states]);

    return null;
};
